import React, { useState } from "react";
import { getData } from "country-list";
import { useGetTournaments } from "../apiCalls/tournamentApiCalls";
import { useStats } from "../apiCalls/statsApiCalls";
import {
  Container,
  Card,
  Button,
  Input,
  Select,
  SearchIcon,
  StatStrip,
} from "../components/ui";
import Pagination from "../components/ui/Pagination";
import {
  CalendarIcon,
  GlobeIcon,
  TrophyIcon,
  UsersIcon,
} from "../components/ui/icons";
import TournamentCard from "../components/TournamentCard";
import { formatNumber } from "../utils";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";

const countries = getData();
const PAGE_SIZE = 12;

const TABS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
  { value: "", label: "All Tournaments" },
];

const CATEGORIES = ["Premier Padel", "Master", "Challenger"];

const SearchTournaments = () => {
  const [status, setStatus] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useGetTournaments({
    status,
    search,
    country,
    category,
    page,
    limit: PAGE_SIZE,
  });
  const { data: statsRes } = useStats();
  const stats = statsRes?.data;

  const tournaments = data?.data ?? [];

  const withReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <>
      <Container className="pt-10 pb-8 md:pt-12">
        <div className="flex flex-col gap-8 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-navy md:text-4xl">
              Tournaments
            </h1>
            <p className="mt-3 max-w-lg text-base text-muted">
              Discover upcoming and ongoing padel tournaments around the world.
              Compete. Earn points. Climb the rankings.
            </p>
          </div>

          <div className="w-full xl:max-w-2xl">
            <StatStrip
              items={[
                {
                  icon: <CalendarIcon />,
                  value: formatNumber(stats?.tournaments ?? 0),
                  label: "Tournaments",
                },
                {
                  icon: <GlobeIcon />,
                  value: formatNumber(stats?.countries ?? 0),
                  label: "Countries",
                },
                {
                  icon: <TrophyIcon className="text-gold" />,
                  value: formatNumber(stats?.clubs ?? 0),
                  label: "Host Clubs",
                },
                {
                  icon: <UsersIcon />,
                  value: formatNumber(stats?.rankedPlayers ?? 0),
                  label: "Ranked Players",
                },
              ]}
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-6 border-b border-border">
          {TABS.map((tab) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => withReset(setStatus)(tab.value)}
              className={`-mb-px border-b-2 pb-3 text-sm font-semibold transition-colors ${
                status === tab.value
                  ? "border-primary text-primary"
                  : "border-transparent text-muted hover:text-navy"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </Container>

      <Container className="pb-16 md:pb-20">
        <Card className="mb-6">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Input
              icon={<SearchIcon />}
              placeholder="Search tournaments…"
              value={search}
              onChange={(e) => withReset(setSearch)(e.target.value)}
              aria-label="Search tournaments"
            />
            <Select
              value={country}
              onChange={(e) => withReset(setCountry)(e.target.value)}
              aria-label="Filter by country"
            >
              <option value="">All Countries</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select
              value={category}
              onChange={(e) => withReset(setCategory)(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        {isLoading && <Loader label="Loading tournaments…" />}
        {isError && <ErrorState message="We couldn't load the tournaments." />}
        {!isLoading && !isError && tournaments.length === 0 && (
          <EmptyState
            icon="🏆"
            message="No tournaments match your filters."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStatus("");
                  setSearch("");
                  setCountry("");
                  setCategory("");
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            }
          />
        )}

        {tournaments.length > 0 && (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tournaments.map((tournament) => (
                <TournamentCard key={tournament._id} tournament={tournament} />
              ))}
            </div>
            <Pagination
              page={page}
              totalPages={data?.totalPages ?? 1}
              onChange={setPage}
              className="mt-10"
            />
          </>
        )}

        <Card className="mt-10 flex flex-col items-center gap-5 bg-primary-wash md:flex-row md:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <TrophyIcon />
            </span>
            <div>
              <h2 className="font-semibold text-navy">
                Organizing a tournament?
              </h2>
              <p className="mt-1 text-sm text-muted">
                Get listed on PadelRank and attract top players from around the
                world.
              </p>
            </div>
          </div>
          <Button to="/how-rankings-work" variant="secondary">
            Add Your Tournament →
          </Button>
        </Card>
      </Container>
    </>
  );
};

export default SearchTournaments;
